import type {
  VideoGenerationMetadataItem,
  VideoGenerationPayload,
} from '../api/seedance';
import type { MatNode } from '../store/seedanceTypes';

/**
 * 生成视频 content 中的参考素材条目（不含文案 text）。
 * 图片：{ type: "image_url", image_url: { url }, role: "reference_image" }
 * 视频：{ type: "video_url", video_url: { url }, role: "reference_video" }
 * 音频：{ type: "audio_url", audio_url: { url }, role: "reference_audio" }
 *
 * 素材库来源的节点必须带 `sourceAssetId`，由后端按 id 解析；无 id 时退化为直链 url。
 */
export const buildVideoGenerationMetadataItems = (
  matNodes: MatNode[],
): VideoGenerationMetadataItem[] => {
  const connected = matNodes.filter((n) => n.connected);
  const items: VideoGenerationMetadataItem[] = [];

  for (const n of connected) {
    const url = n.sourceAssetId?.trim()
      ? `asset://${n.sourceAssetId.trim()}`
      : (n.url || '').trim() || undefined;
    if (!url) continue;

    if (n.type === 'image') {
      items.push({
        type: 'image_url',
        image_url: { url },
        role: 'reference_image',
      });
    } else if (n.type === 'video') {
      items.push({
        type: 'video_url',
        video_url: { url },
        role: 'reference_video',
      });
    } else if (n.type === 'audio') {
      items.push({
        type: 'audio_url',
        audio_url: { url },
        role: 'reference_audio',
      });
    }
  }

  return items;
};

/** @deprecated 使用 {@link buildVideoGenerationMetadataItems} */
export const buildGenerationsMediaFields = (
  matNodes: MatNode[],
): Pick<VideoGenerationPayload, 'metadataItems'> => {
  const metadataItems = buildVideoGenerationMetadataItems(matNodes);
  if (metadataItems.length === 0) return {};
  return { metadataItems };
};

/** 调试日志输出前截断大字段，避免 data URL 或长响应刷屏。 */
export const jsonStringifyForDebug = (v: unknown, maxLen = 400) => {
  const s = JSON.stringify(v, (_, x) => {
    if (typeof x === 'string' && x.length > 120 && (x.startsWith('data:') || x.length > 2000)) {
      return `${x.slice(0, 64)}…[已省略]`;
    }
    return x;
  });
  return s.length > maxLen ? s.slice(0, maxLen) + '…' : s;
};
